if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.currentIndex = 0;
        this.scrollTarget = null;

        // The nav buttons live outside `.product-media-modal__content`, so they
        // survive the innerHTML swap product-info.js does on a variant change.
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');

        [this.prevButton, this.nextButton].forEach((button, index) => {
          if (!button) return;
          // ModalDialog closes `.media-modal` on any pointerup outside the media
          // itself — stop the arrows from counting as a click-away.
          button.addEventListener('pointerup', (event) => event.stopPropagation());
          button.addEventListener('click', () => this.goTo(this.currentIndex + (index === 0 ? -1 : 1)));
        });

        this.addEventListener('keyup', (event) => {
          if (event.code === 'ArrowLeft') this.goTo(this.currentIndex - 1);
          if (event.code === 'ArrowRight') this.goTo(this.currentIndex + 1);
        });

        const track = this.track;
        if (!track) return;

        // A swipe or wheel during our own scroll hands control back to the user.
        ['pointerdown', 'wheel', 'touchstart'].forEach((type) => {
          track.addEventListener(type, () => (this.scrollTarget = null), { passive: true });
        });

        // `scrollend` is the precise signal, but it isn't fired everywhere, so
        // back it with a debounce. Settling twice is a no-op.
        if ('onscrollend' in window) {
          track.addEventListener('scrollend', this.onScrollSettled.bind(this));
        }
        track.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(this.onScrollSettled.bind(this), 120);
          },
          { passive: true }
        );
      }

      get track() {
        return this.querySelector('.product-media-modal__content');
      }

      get slides() {
        return Array.from(this.querySelectorAll('.product-media-modal__slide')).filter(
          (slide) => slide.offsetParent !== null || slide.getClientRects().length > 0
        );
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);

        this.goTo(index === -1 ? 0 : index, 'auto');
      }

      offsetOf(slide) {
        return slide.offsetLeft - this.track.offsetLeft;
      }

      goTo(index, behavior = 'smooth') {
        const slides = this.slides;
        if (!slides.length || !this.track) return;

        this.currentIndex = Math.min(Math.max(index, 0), slides.length - 1);

        const slide = slides[this.currentIndex];
        const target = this.offsetOf(slide);

        // The arrows and the active slide update immediately; the scroll catches up.
        this.setActiveSlide(slide);
        this.updateNav(slides);

        if (Math.abs(this.track.scrollLeft - target) <= 1) {
          this.scrollTarget = null;
          return;
        }

        this.scrollTarget = target;
        this.track.scrollTo({ left: target, behavior });

        if (behavior === 'smooth') this.ensureScrollStarted(target);
      }

      /*
       * Some engines drop a smooth programmatic scroll on a mandatory snap
       * container, which would leave the arrows looking dead. Only step in when
       * the scroll never moved at all — never interrupt one that is animating.
       */
      ensureScrollStarted(target) {
        clearTimeout(this.startTimeout);

        const startedAt = this.track.scrollLeft;
        this.startTimeout = setTimeout(() => {
          if (this.scrollTarget !== target) return;
          if (this.track.scrollLeft !== startedAt) return;

          this.track.scrollTo({ left: target, behavior: 'auto' });
        }, 150);
      }

      // Keeps the active slide in sync when the carousel is swiped or scrolled
      // directly rather than driven through the arrows.
      onScrollSettled() {
        const slides = this.slides;
        if (!slides.length || !this.track) return;

        // A scroll we started ourselves has only settled once it reaches its
        // target — ignore every position it passes through on the way there.
        if (this.scrollTarget !== null) {
          if (Math.abs(this.track.scrollLeft - this.scrollTarget) > 2) return;
          this.scrollTarget = null;
        }

        const center = this.track.scrollLeft + this.track.clientWidth / 2;
        const index = slides.findIndex((slide) => {
          const start = this.offsetOf(slide);
          return center >= start && center < start + slide.offsetWidth;
        });
        if (index === -1 || index === this.currentIndex) return;

        this.currentIndex = index;
        this.setActiveSlide(slides[index]);
        this.updateNav(slides);
      }

      setActiveSlide(activeSlide) {
        // Only on a real change, so a stray scroll can't stop what is playing.
        if (!activeSlide.classList.contains('active')) {
          this.querySelectorAll('.product-media-modal__slide').forEach((slide) => {
            slide.classList.toggle('active', slide === activeSlide);
          });

          // Stop whatever was playing on the slide we just left.
          window.pauseAllMedia();
        }

        // Always, even when the slide was already active: closing the modal
        // pauses the video without clearing the class, so reopening on the same
        // slide has to start it again.
        this.playActiveMedia(activeSlide);
      }

      /*
       * Play as soon as the slide is shown rather than waiting for a click on
       * the poster, so the viewer never sees a play button. `false` keeps focus
       * on the modal instead of moving it to the <video>. loadContent only
       * builds the player once, so replay it by hand on the way back.
       */
      playActiveMedia(activeSlide) {
        const media = activeSlide.querySelector('deferred-media, product-model') || activeSlide.firstElementChild;
        if (media?.nodeName !== 'DEFERRED-MEDIA') return;

        media.loadContent(false);
        const video = media.querySelector('video');
        if (video) video.play().catch(() => {});
      }

      updateNav(slides) {
        const single = slides.length <= 1;
        [this.prevButton, this.nextButton].forEach((button) => {
          if (button) button.hidden = single;
        });
        if (this.prevButton) this.prevButton.disabled = this.currentIndex === 0;
        if (this.nextButton) this.nextButton.disabled = this.currentIndex === slides.length - 1;
      }
    }
  );
}
